/**
 * ClientChangeConfirmDialog
 *
 * Shown when a user attempts to change the client on a project that has
 * financial activity (approved timesheets or active status) but does NOT
 * meet the hard-block criteria (invoices raised / revenue recognised / open WIP).
 *
 * Hard-block cases never reach this dialog — the client field is disabled upstream.
 */

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";
import type { Client } from "@/types";

interface ClientChangeConfirmDialogProps {
  open: boolean;
  previousClient: Client | null;
  newClient: Client | null;
  /** Approved hours on the project — shown in the dialog body */
  actualHours: number;
  onConfirm: () => void;
  onCancel: () => void;
}

function formatTerms(client: Client | null): string {
  if (!client) return "—";
  if (client.dueDateType === "days-from-invoice" && client.dueDateDays) {
    return `${client.dueDateDays} days from invoice`;
  }
  if (client.dueDateType === "end-of-following-month") return "End of following month";
  if (client.dueDateType === "day-of-following-month" && client.dueDateDays) {
    return `Day ${client.dueDateDays} of following month`;
  }
  if (client.dueDateType === "days-after-end-of-month" && client.dueDateDays) {
    return `${client.dueDateDays} days after end of month`;
  }
  return "—";
}

export function ClientChangeConfirmDialog({
  open,
  previousClient,
  newClient,
  actualHours,
  onConfirm,
  onCancel,
}: ClientChangeConfirmDialogProps) {
  const oldTerms = formatTerms(previousClient);
  const newTerms = formatTerms(newClient);
  const termsChange = oldTerms !== newTerms && oldTerms !== "—" && newTerms !== "—";

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-warning-500" />
            Client Change — Please Confirm
          </DialogTitle>
          <DialogDescription>
            Changing the client on an active project may affect how time and cost
            transactions are reported.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* From → To */}
          <div className="rounded-md border bg-muted/40 p-3 space-y-1">
            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground w-16 shrink-0">From</span>
              <span className="font-medium">
                {previousClient?.name ?? "—"}
                {previousClient?.code ? ` (${previousClient.code})` : ""}
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground w-16 shrink-0">To</span>
              <span className="font-medium">
                {newClient?.name ?? "—"}
                {newClient?.code ? ` (${newClient.code})` : ""}
              </span>
            </div>
          </div>

          {/* Activity summary */}
          <div className="space-y-1.5">
            <p className="text-sm font-medium">The following activity exists on this project:</p>
            <ul className="text-sm text-muted-foreground space-y-1 ml-4 list-disc">
              {actualHours > 0 && (
                <li>{actualHours.toLocaleString()} approved timesheet hours</li>
              )}
              {termsChange && (
                <li>
                  Payment terms will change from{" "}
                  <span className="text-foreground font-medium">{oldTerms}</span>
                  {" → "}
                  <span className="text-foreground font-medium">{newTerms}</span>
                </li>
              )}
            </ul>
          </div>

          <p className="text-sm text-muted-foreground">
            Existing transactions will not be moved. This change will be recorded in the project
            audit log.
          </p>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="default" onClick={onConfirm}>
            Yes, Change Client
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
